import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import axiosInstance from "../../axiosInstance";

// --- DEBUG FLAG ---
const DEBUG_MODE = false;

class WebSocketService {
  constructor() {
    this.stompClient = null;
    this.connected = false;
    this.subscriptions = {};
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = null;
    this.isConnecting = false;
    this.tokenRefreshing = false;
    this.pendingSubscriptions = []; // Store subscriptions to recreate on reconnect
  }

  // Only logs if DEBUG_MODE is true
  log(...args) {
    if (DEBUG_MODE) console.log("[WebSocketService]", ...args);
  }

  error(...args) {
    if (DEBUG_MODE) console.error("[WebSocketService:ERROR]", ...args);
  }

  getAuthHeaders() {
    const token = sessionStorage.getItem("jwtToken");
    const tenantId = sessionStorage.getItem("tenantId");
    if (!token || !tenantId) return {};
    return { Authorization: token, "tenant-id": tenantId };
  }

  async refreshToken() {
    if (this.tokenRefreshing) return;
    this.tokenRefreshing = true;

    try {
      const resp = await axiosInstance.post(
        `${process.env.REACT_APP_BACKEND_URL}/refresh-token`,
        {},
        { withCredentials: true },
      );
      const newToken = resp.headers["authorization"];
      if (newToken) sessionStorage.setItem("jwtToken", newToken);
      this.tokenRefreshing = false;
      this.log("JWT token refreshed successfully.");
      return !!newToken;
    } catch (err) {
      this.error("Failed to refresh token:", err);
      this.tokenRefreshing = false;
      return false;
    }
  }

  /**
   * Connect to WebSocket server
   * @param {Object} config - Connection configuration
   * @param {string} config.tenantId - Tenant ID
   * @param {string} config.empId - Employee ID (optional for some topics)
   * @param {Function} [config.onConnect] - Called every time the socket
   *   becomes connected, including synchronously (before this function
   *   returns) if it's already connected when you call connect(). Callers
   *   that just want to know "is it up right now, and tell me the moment
   *   it changes" should use this instead of polling isConnected().
   * @param {Function} [config.onDisconnect] - Called whenever the socket
   *   goes down (close or error), before any auto-reconnect attempt.
   */
  connect(config) {
    const { tenantId, onConnect, onDisconnect } = config;

    if (!tenantId) {
      this.error("Missing tenantId");
      return;
    }

    if (this.isConnecting || this.connected) {
      this.log("Already connecting or connected");
      // A caller that just mounted (or whose effect just re-ran) still
      // needs to know the current state — without this, a component that
      // asks to connect while we're already connected would never hear
      // onConnect fire and could get stuck thinking it's offline.
      if (this.connected) onConnect?.();
      return;
    }

    this.isConnecting = true;

    const url = `${process.env.REACT_APP_BACKEND_URL}/websocket`;
    this.log("Connecting to WebSocket:", url);

    const socket = new SockJS(url);

    const client = new Client({
      webSocketFactory: () => socket,
      connectHeaders: this.getAuthHeaders(),
      debug: (msg) => this.log("STOMP:", msg),
      reconnectDelay: 0,
      onConnect: () => {
        this.connected = true;
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.log("✅ WebSocket connected");

        // Resubscribe to all pending subscriptions
        this.pendingSubscriptions.forEach(({ topic, key, callback }) => {
          this.subscribeToTopic(topic, key, callback);
        });

        onConnect?.();
      },
      onStompError: async (frame) => {
        this.error("STOMP error:", frame.headers["message"], frame.body);
        const isTokenError =
          frame.headers["message"]?.includes("JWT") ||
          frame.headers["message"]?.includes("Authorization");

        await this.handleReconnect(isTokenError, config);
      },
      onWebSocketClose: () => {
        this.connected = false;
        this.isConnecting = false;
        this.log("WebSocket closed");
        onDisconnect?.();
        this.handleReconnect(false, config);
      },
      onWebSocketError: async (evt) => {
        this.error("WebSocket error:", evt);
        this.connected = false;
        this.isConnecting = false;
        onDisconnect?.();
        await this.handleReconnect(false, config);
      },
    });

    client.activate();
    this.stompClient = client;
  }

  /**
   * Subscribe to a specific topic
   * @param {string} topic - The full topic path (e.g., '/topic/123/enquiry/updates/456/')
   * @param {string} key - Unique key for this subscription
   * @param {Function} callback - Callback function to handle messages
   */
  subscribeToTopic(topic, key, callback) {
    if (!this.connected || !this.stompClient) {
      this.log(`Not connected, storing subscription for later: ${topic}`);
      // Store for later when connection is established
      const existingIndex = this.pendingSubscriptions.findIndex(
        (sub) => sub.key === key,
      );
      if (existingIndex >= 0) {
        this.pendingSubscriptions[existingIndex] = { topic, key, callback };
      } else {
        this.pendingSubscriptions.push({ topic, key, callback });
      }
      return;
    }

    // Unsubscribe existing subscription with same key if exists
    if (this.subscriptions[key]) {
      this.subscriptions[key].unsubscribe();
      this.log(`Unsubscribed from previous ${key}`);
    }

    try {
      this.log(`Subscribing to: ${topic}`);
      const subscription = this.stompClient.subscribe(topic, (msg) => {
        console.log("========== WEBSOCKET MESSAGE ==========");
        console.log("Destination:", topic);
        console.log("Headers:", msg.headers);
        console.log("Raw Body:", msg.body);

        try {
          const data = JSON.parse(msg.body);
          console.log("Parsed:", data);
          callback?.(data);
        } catch (e) {
          console.log("Non JSON:", msg.body);
          callback?.(msg.body);
        }
      });
      this.subscriptions[key] = subscription;

      // Also store in pending subscriptions for reconnection
      const existingIndex = this.pendingSubscriptions.findIndex(
        (sub) => sub.key === key,
      );
      if (existingIndex >= 0) {
        this.pendingSubscriptions[existingIndex] = { topic, key, callback };
      } else {
        this.pendingSubscriptions.push({ topic, key, callback });
      }

      this.log(`✅ Subscribed to ${key}: ${topic}`);
    } catch (err) {
      this.error(`Failed to subscribe to ${topic}:`, err);
    }
  }

  /**
   * Unsubscribe from a specific topic
   * @param {string} key - The subscription key
   */
  unsubscribeFromTopic(key) {
    if (this.subscriptions[key]) {
      this.subscriptions[key].unsubscribe();
      delete this.subscriptions[key];
      this.log(`Unsubscribed from ${key}`);
    }

    // Remove from pending subscriptions
    this.pendingSubscriptions = this.pendingSubscriptions.filter(
      (sub) => sub.key !== key,
    );
  }

  /**
   * Helper method to subscribe to enquiry updates for an employee
   */
  subscribeToEnquiryUpdates(tenantId, empId, callback) {
    const topic = `/topic/${tenantId}/enquiries/updates/${empId}/`;
    this.subscribeToTopic(topic, "enquiry", callback);
  }

  /**
   * Helper method to subscribe to operations updates for an employee
   */
  subscribeToOperationsUpdates(tenantId, empId, callback) {
    const topic = `/topic/${tenantId}/operations/updates/${empId}/`;
    this.subscribeToTopic(topic, "operations", callback);
  }

  /**
   * Helper method to subscribe to manager (team) enquiry updates.
   * Backend method: getManagerEnquiryStageHistoryWebSocketResponse
   */
  subscribeToManagerEnquiryUpdates(tenantId, empId, callback) {
    const topic = `/topic/${tenantId}/manager/enquiry/updates/${empId}/`;
    this.subscribeToTopic(topic, "manager-enquiry", callback);
  }

  /**
   * Helper method to subscribe to team operations updates
   */
  subscribeToTeamOperationsUpdates(tenantId, callback) {
    const topic = `/topic/${tenantId}/operations/all/updates/`;
    this.subscribeToTopic(topic, "operations-team", callback);
  }

  async handleReconnect(isTokenError, config) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.error("Max reconnect attempts reached, giving up");
      return;
    }

    if (isTokenError) {
      const refreshed = await this.refreshToken();
      if (!refreshed) return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(2000 * 2 ** (this.reconnectAttempts - 1), 30000);
    this.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = setTimeout(() => {
      this.connect(config);
    }, delay);
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.stompClient) {
      // Unsubscribe from all active subscriptions
      Object.values(this.subscriptions).forEach((sub) => {
        try {
          sub.unsubscribe();
        } catch (err) {
          this.error("Error unsubscribing:", err);
        }
      });
      this.subscriptions = {};

      // Deactivate the client
      if (this.connected) {
        try {
          this.stompClient.deactivate();
        } catch (err) {
          this.error("Error deactivating client:", err);
        }
      }

      this.stompClient = null;
      this.connected = false;
      this.isConnecting = false;
      this.log("❌ WebSocket disconnected");
    }

    // Clear pending subscriptions
    this.pendingSubscriptions = [];
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected() {
    return this.connected;
  }
}

const webSocketService = new WebSocketService();
export default webSocketService;
